import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import BrandMark from "../components/BrandMark";
import CodeVerifyForm from "../components/CodeVerifyForm";
import { toast } from "sonner";

function formatError(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login, verifyLogin, resendCode } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [step, setStep] = useState("credentials"); // credentials | code
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const from = loc.state?.from || "/orders";

  const submitCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await login(email, password);
      if (r?.codeRequired) {
        setPendingEmail(r.email);
        setStep("code");
        toast.success("Code sent — check your email");
      } else {
        toast.success("Welcome back");
        nav(from);
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (code) => {
    await verifyLogin(pendingEmail, code);
    toast.success("Welcome back");
    nav(from);
  };

  return (
    <div data-testid="login-page" className="pt-32 pb-24 min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md p-10 bg-[#09120C] border border-[#1A2B20]">
        <BrandMark variant="knot" className="h-12 w-12 mb-6 block" />

        {step === "credentials" ? (
          <form onSubmit={submitCredentials}>
            <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-2">Welcome back</div>
            <h1 className="font-serif-display text-3xl text-white mb-8">Login</h1>

            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Email</label>
            <input
              data-testid="login-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:border-[#279153] focus:outline-none mb-5"
            />

            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Password</label>
            <input
              data-testid="login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:border-[#279153] focus:outline-none mb-6"
            />

            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full px-6 py-4 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase disabled:opacity-50"
            >
              {loading ? "…" : "Continue"}
            </button>

            <div className="mt-4 text-center">
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs tracking-[0.2em] uppercase text-[#95A89C] hover:text-[#279153]">
                Forgot password?
              </Link>
            </div>

            <p className="mt-6 text-sm text-[#95A89C] text-center">
              New to Grá?{" "}
              <Link to="/register" data-testid="login-go-register" className="text-[#279153] hover:underline">
                Create an account
              </Link>
            </p>
          </form>
        ) : (
          <CodeVerifyForm
            email={pendingEmail}
            purpose="login"
            submitLabel="Verify & sign in"
            testidPrefix="login-code"
            onSubmit={verifyCode}
            onResend={() => resendCode(pendingEmail, "login")}
            onBack={() => setStep("credentials")}
          />
        )}
      </div>
    </div>
  );
}
